import { Link, useRouterState } from "@tanstack/react-router";
import { useLocale } from "@/i18n/LocaleContext";
import { PROVINSI_COUNT, KOTA_COUNT, KECAMATAN_COUNT } from "@/data/locations-counts";
import { detectCluster, getClusterTheme, type Cluster } from "@/lib/cluster";
import { currentHijriYear } from "@/lib/calendar/hijri";

type LinkItem = { to: string; label: string; external?: boolean };

/**
 * Set of link labels already shown by ClusterFooter for the active cluster.
 * Footer's "Jelajah" column filters these out so no label appears twice
 * on a single page.
 */
function clusterOwnedLabels(cluster: Cluster, y: number): Set<string> {
  if (!cluster) return new Set();
  if (cluster === "calendar") {
    return new Set([
      `Tanggal Merah ${y}`,
      `Libur Nasional ${y}`,
      `Cuti Bersama ${y}`,
      `Kalender ${y - 1}`,
      `Kalender ${y + 1}`,
    ]);
  }
  if (cluster === "hijri" || cluster === "prayer") {
    return new Set(["Jadwal Imsakiyah", "Jadwal Buka Puasa"]);
  }
  return new Set();
}

export function Footer() {
  const currentYear = new Date().getFullYear();
  const { t, href, locale } = useLocale();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const cluster = detectCluster(pathname);
  const theme = getClusterTheme(cluster);
  const hy = currentHijriYear();
  const totalPages = 5000 + KOTA_COUNT * 2 + KECAMATAN_COUNT * 2;
  const totalLabel =
    totalPages > 5000 ? `${Math.floor(totalPages / 1000)}.000+ halaman` : "5.000+ halaman";

  // Kol 1 — Utama (entry per cluster)
  const coreLinks: LinkItem[] = [
    { to: href("/"), label: locale === "en" ? "Home" : "Beranda" },
    { to: href("/hari-ini"), label: locale === "en" ? "Today" : "Hari Ini" },
    { to: href(`/kalender/${currentYear}`), label: `Kalender ${currentYear}` },
    { to: href("/kalender-hijriah"), label: "Kalender Hijriah" },
    { to: "/jadwal-sholat/", label: "Jadwal Sholat" },
    { to: href("/hari"), label: locale === "en" ? "Notable Days" : "Hari Penting" },
    { to: `/harpitnas/${currentYear}/`, label: `Harpitnas ${currentYear}` },
  ];

  // Kol 2 — Jelajah Tahun (auto-filtered against active cluster)
  const owned = clusterOwnedLabels(cluster, currentYear);
  const exploreLinks: LinkItem[] = [
    { to: href(`/kalender/${currentYear - 1}`), label: `Kalender ${currentYear - 1}` },
    { to: href(`/kalender/${currentYear + 1}`), label: `Kalender ${currentYear + 1}` },
    { to: href(`/tanggal-merah/${currentYear}`), label: `Tanggal Merah ${currentYear}` },
    { to: href(`/libur-nasional/${currentYear}`), label: `Libur Nasional ${currentYear}` },
    { to: href(`/cuti-bersama/${currentYear}`), label: `Cuti Bersama ${currentYear}` },
    { to: href(`/kalender-hijriah/${hy}/`), label: `Kalender Hijriah ${hy} H` },
    { to: "/jadwal-imsak/", label: "Jadwal Imsakiyah" },
    { to: "/jadwal-buka-puasa/", label: "Jadwal Buka Puasa" },
  ].filter((l) => !owned.has(l.label));

  // Kol 3 — Tools & Zodiak
  const toolsLinks: LinkItem[] = [
    { to: href("/tools/kalkulator-hari"), label: "Kalkulator Hari" },
    { to: href("/tools/cek-weton"), label: "Cek Weton" },
    { to: href("/tools/hari-lahir"), label: "Hari Lahir" },
    { to: href("/tools/hari-jadian"), label: "Hari Jadian" },
    { to: href("/tools/hari-menikah"), label: "Lama Menikah" },
    { to: "/zodiak/", label: "Daftar Zodiak" },
    { to: "/zodiak/cocok/", label: "Kecocokan Zodiak" },
    { to: href("/tools"), label: locale === "en" ? "All Tools" : "Semua Tools" },
  ];

  // Kol 4 — Legal & Info situs
  const legalLinks: LinkItem[] = [
    { to: href("/tentang"), label: "Tentang Kami" },
    { to: href("/kontak"), label: "Kontak" },
    { to: "/api/sitemap.xml", label: "Sitemap", external: true },
    { to: href("/kebijakan-privasi"), label: t.footer.privacy },
    { to: href("/syarat-ketentuan"), label: t.footer.terms },
    { to: "/kontak/?topic=koreksi", label: "Laporkan kesalahan redaksi" },
  ];

  const columns: Array<{ heading: string; links: LinkItem[] }> = [
    { heading: locale === "en" ? "Main" : "Utama", links: coreLinks },
    { heading: locale === "en" ? "Explore" : "Jelajah", links: exploreLinks },
    { heading: locale === "en" ? "Tools & Zodiac" : "Tools & Zodiak", links: toolsLinks },
    { heading: t.footer.legal, links: legalLinks },
  ];

  return (
    <footer className={`mt-16 border-t ${cluster ? theme.accentBorder : "border-border"} bg-card/50`}>
      {/* 4-column site-wide tier. Cluster deep-links live in ClusterFooter above. */}
      <div className="container mx-auto grid gap-8 px-4 py-10 sm:grid-cols-2 lg:grid-cols-4">
        {columns.map((col) => (
          <div key={col.heading}>
            <h3 className="mb-2 text-sm font-semibold">{col.heading}</h3>
            <ul className="space-y-1 text-sm">
              {col.links.map((l) => (
                  <li key={l.to + l.label}>
                    {l.external ? (
                      <a href={l.to} className="text-muted-foreground hover:text-primary">
                        {l.label}
                      </a>
                    ) : (
                      <Link to={l.to as never} className="text-muted-foreground hover:text-primary">
                        {l.label}
                      </Link>
                    )}
                  </li>
              ))}
              {col.heading === t.footer.legal && (
                <li className="pt-2 text-xs text-muted-foreground">{t.footer.sourceData}</li>
              )}
            </ul>
          </div>
        ))}
      </div>

      {/* Copyright + brand stats */}
      <div className="border-t border-border">
        <div className="container mx-auto px-4 py-6 text-center">
          <p className="mx-auto max-w-3xl text-xs text-muted-foreground">
            © {currentYear}{" "}
            <Link to={href("/") as never} className="font-medium hover:underline">
              hari.co.id
            </Link>{" "}
            — Kalender Indonesia, Hijriah, Jawa, Bali & Jadwal Sholat. Data libur bersifat
            referensi, harap verifikasi ulang ke sumber resmi (SKB 3 Menteri / Kemenag) untuk
            keperluan administratif.
          </p>
          <p className="mt-2 text-[11px] text-muted-foreground">
            {totalLabel} · {PROVINSI_COUNT} provinsi · {KOTA_COUNT}+ kota/kabupaten ·{" "}
            {KECAMATAN_COUNT}+ kecamatan
          </p>
        </div>
      </div>
    </footer>
  );
}
